import { Checkbox, Col, DatePicker, Divider, Form, Input, InputNumber, Modal, notification, Row } from "antd";
import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { coreApi } from "../../api/calls";
import PartnerSelect from "../Partners/PartnerSelect";
import CleaningPromoTagSelect from "./CleaningPromoTagSelect";

/**
 * One Time Cleaning Add Form
 * @component
 * @alias OneTimeAddForm
 * @property {bool} isOpen
 * @property {function} close
 * @property {object} buildingUnit
 * @property {date} selectedDate
 * @property {function} refreshCalendarView
 * @returns <Modal /> with <Form /> inside for adding a one-time cleaning
 */
const OneTimeCleaningAddForm = ({ isOpen, close, buildingUnit, selectedDate, refreshCalendarView }) => {
  const { t } = useTranslation();

  const [isSubmitting, setSubmitting] = useState(false);

  const [oneTimeCleaningForm] = Form.useForm();

  return (
    <Modal
      width={650}
      open={isOpen}
      title={t("cleanings.oneTime.createCleaning")}
      okText={t("common.create")}
      onOk={() => {
        oneTimeCleaningForm.submit();
      }}
      okButtonProps={{ loading: isSubmitting }}
      onCancel={() => {
        oneTimeCleaningForm.resetFields();
        close();
      }}
    >
      <Form
        layout={"horizontal"}
        form={oneTimeCleaningForm}
        onFinish={(values) => {
          setSubmitting(true);
          // again stupid moment.js null check 'cause it can NOT handle NULL
          values?.date_expected
            ? (values.date_expected = moment(values.date_expected).format("YYYY-MM-DD"))
            : (values.date_expected = null);

          values?.date_real
            ? (values.date_real = moment(values.date_real).format("YYYY-MM-DD"))
            : (values.date_real = null);
          coreApi
            .post("/cleanings/one-time", values)
            .then((response) => {
              notification.success({
                message: response.data.message,
              });
              refreshCalendarView();
              oneTimeCleaningForm.resetFields();
              close();
            })
            .catch((error) => {
              notification.error({
                message: error.response.data.message,
              });
            })
            .finally(() => {
              setSubmitting(false);
            });
        }}
      >
        <Form.Item hidden name={"building_unit_id"} initialValue={buildingUnit?.id}>
          <Input></Input>
        </Form.Item>
        <Row gutter={16}>
          <Col xs={24} md={12}>
            <PartnerSelect
              name={"partner_id"}
              label={t("cleanings.partner")}
              validationMessage={t("cleanings.validation.partners")}
              required={true}
            />
          </Col>
          <Col xs={24} md={12}>
            <PartnerSelect name={"substitute_id"} label={t("cleanings.substitute")} />
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name={"date_expected"} label={t("cleanings.dateExpected")} initialValue={selectedDate}>
              <DatePicker disabled bordered={false} format="L" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Form.Item name={"date_real"} label={t("cleanings.dateReal")} initialValue={selectedDate}>
              <DatePicker format="L" />
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Divider orientation={"left"}>{t("cleanings.values")}</Divider>
            <Form.Item name={"price"} label={t("cleanings.price")}>
              <InputNumber />
            </Form.Item>
            <Form.Item name={"promo"} label={t("cleanings.promo")}>
              <InputNumber />
            </Form.Item>
            <Form.Item name={"service_ratio"} label={t("cleanings.serviceRatio")} initialValue={"0.4"}>
              <InputNumber></InputNumber>
            </Form.Item>
          </Col>
          <Col xs={24} md={12}>
            <Divider orientation={"left"}>{t("cleanings.promoCoveredBy")}</Divider>
            <Form.Item
              name={"is_promo_partner_covered"}
              valuePropName={"checked"}
              label={t("cleanings.promoCoveredByPartner")}
            >
              <Checkbox></Checkbox>
            </Form.Item>
            <Form.Item
              name={"is_promo_anna_covered"}
              valuePropName={"checked"}
              label={t("cleanings.promoCoveredByAnna")}
            >
              <Checkbox></Checkbox>
            </Form.Item>
            <Form.Item name={"is_full_partner_share"} valuePropName={"checked"} label={t("cleanings.fullPartnerShare")}>
              <Checkbox></Checkbox>
            </Form.Item>
          </Col>
          <Col span={24}>
            <CleaningPromoTagSelect />
          </Col>
          <Col span={24}>
            <Form.Item
              name="notes"
              label={t("cleanings.oneTime.cleanedInvoiceItemName")}
              rules={[
                { required: true, message: t("common.validation.required") },
                { min: 3, message: t("common.validation.min", { min: 3 }) },
                { max: 250, message: t("common.validation.max", { max: 250 }) },
              ]}
            >
              <Input.TextArea rows={3} showCount minLength={3} maxLength={250} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
    </Modal>
  );
};

export default OneTimeCleaningAddForm;
